import React, { useEffect, useRef } from "react";

import { Button, Caption, Card, Flex } from "@olist/design-system";

import { GradientTextAnimated, Message } from "./styles";
import type { ChatMessage, ChatSender } from "../../types/chat";

export function EnhancedChat({
  error,
  loading,
  messages,
  sendMessage,
  theme,
  typing,
}: {
  theme?: "light" | "dark";
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  typing: boolean;
  sendMessage: (text: string) => Promise<void>;
}) {
  const chatRef = useRef<HTMLDivElement>(null);

  const getSenderLabel = (from: ChatSender) => {
    if (from === 'user') return "Você";
    if (from === 'agent') return "Atendente";
    return "Sistema"
  }

  const handleSend = async (text: string) => {
    if (!text.trim()) return;
    await sendMessage(text);
  };

  useEffect(() => {
    chatRef.current?.scrollTo({
      top: chatRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length]);

  if (loading)
    return (
      <Flex gap="inline.small">
        <GradientTextAnimated DSTheme={theme}>
          Conectando ao chat...
        </GradientTextAnimated>
      </Flex>
    );
  if (error) return <div>Erro: {error}</div>;

  return (
    <>
      {messages.map((m) => {
        if (m.type === "Text") {
          return (
            <Flex
              key={m.id ?? m.text}
              flexDirection="column"
              gap="stack.small"
              id="messages"
            >
              <Flex
                flexDirection="column"
                gap="inline.1xsmall"
                alignItems={m.from === "agent" ? "flex-start" : "flex-end"}
              >
                <Flex gap="inline.1xsmall" alignItems="flex-start">
                  <Caption bold>{getSenderLabel(m.from)}</Caption>
                  <Caption color="neutral.base">
                    {new Date().toLocaleTimeString("pt-br", {
                      timeStyle: "short",
                    })}
                  </Caption>
                </Flex>

                <Flex
                  gap="inline.small"
                  justifyContent={m.from === "user" ? "flex-end" : "flex-start"}
                  alignItems="flex-start"
                >
                  <Message sender={m.from}>{m.text}</Message>
                </Flex>
              </Flex>
            </Flex>
          );
        }
        if (m.type === "Rich") {
          return (
            <div key={m.id ?? m.text}>
              {m.text && (
                <Message sender={m.from}>
                  {m.text}
                </Message>
              )}
              {m.cards && (
                <Flex flexDirection="column" gap="stack.small">
                  {m.cards.map((card, idx) => (
                    <Card key={idx}>
                      <Card.Body>
                        {card.imageUrl && (
                          <img
                            src={card.imageUrl}
                            alt={card.title}
                            className="w-full h-24 object-cover rounded"
                          />
                        )}
                        <Caption>{card.title}</Caption>
                        {card.subtitle && (
                          <Caption color="neutral.base">
                            {card.subtitle}
                          </Caption>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </Flex>
              )}
              {m.buttons && (
                <Flex flexDirection="column" gap="small">
                  {m.buttons.map((b) => (
                    <Button
                      key={b.value}
                      onClick={() => handleSend(b.value)}
                      variation="secondary"
                    >
                      {b.label}
                    </Button>
                  ))}
                </Flex>
              )}
            </div>
          );
        }
        if (m.type === "Choices") {
          return (
            <Flex
              key={m.id ?? m.text}
              flexDirection="column"
              gap="stack.small"
              id="messages"
            >
              <Caption bold>{getSenderLabel(m.from)}</Caption>

              {m.text && (
                <Message sender={m.from}>{m.text}</Message>
              )}

              <Flex flexDirection="column" gap="inline.small">
                {m.choices.map((choice) => (
                  <Button
                    key={choice.optionIdentifier}
                    variation="secondary"
                    onClick={() => handleSend(choice.label)}
                  >
                    {choice.label}
                  </Button>
                ))}
              </Flex>
            </Flex>
          );
        }
        if (m.type === "File") {
          return (
            <Flex
              key={m.id ?? m.fileName}
              flexDirection="column"
              gap="stack.small"
              id="messages"
            >
              <Caption bold>{getSenderLabel(m.from)}</Caption>

              <Card>
                <Card.Body>
                  <Caption>{m.fileName}</Caption>

                  {m.fileUrl && (
                    <a
                      href={m.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#0A84FF" }}
                    >
                      Baixar arquivo
                    </a>
                  )}
                </Card.Body>
              </Card>
            </Flex>
          );
        }
        return null;
      })}

      {typing && (
        <Flex gap="inline.small">
          <GradientTextAnimated DSTheme={theme}>
            Agente digitando...
          </GradientTextAnimated>
        </Flex>
      )}
    </>
  );
}
